import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { TopBar } from '../components/TopBar'
import { Sidebar } from '../components/Sidebar'
import { ContextPanel } from '../components/ContextPanel'
import { AgentPanel } from '../components/AgentPanel'
import { ExportBar } from '../components/ExportBar'
import { useState, useEffect, useRef, useCallback } from 'react'
import { parseMarkdown } from '../utils/markdownConfig'
import { AnnotationViewer } from '../components/AnnotationViewer'
import { ReviewViewer } from '../components/ReviewViewer'
import { NarrationViewer } from '../components/NarrationViewer'
import { StoryViewer } from '../components/StoryViewer'
import { Presentation3DViewer } from '../components/Presentation3DViewer'
import { PRESENTER_3D_ENABLED } from '../config'
import { CollabEditor, colorFromId } from '../components/CollabEditor'
import { CollabReadView } from '../components/CollabReadView'
import { AnnotationTooltip } from '../components/AnnotationTooltip'
import { useMermaidAsync } from '../hooks/useMermaidAsync'
import { useD2Async } from '../hooks/useD2Async'
import { PostShareModal } from '../components/PostShareModal'
import { SectionCreateModal } from '../components/SectionCreateModal'
import { uploadFile } from '../api/storage'
import { NotifyAuthorsPicker } from '../components/NotifyAuthorsPicker'
import { SalienceOverlay } from '../components/SalienceOverlay'
import { CanvasView } from '../components/CanvasView'
import { CanvasCardEditor } from '../components/CanvasCardEditor'
import { ModeToggle, type EditorMode } from '../components/ModeToggle'
import { usePostBlocks } from '../hooks/usePostBlocks'
import { EdgesAutoCreatedToast } from '../components/EdgesAutoCreatedToast'
import { useDocCanvasMorph, type MorphState } from '../hooks/useDocCanvasMorph'
import { useCanvasTriggers } from '../hooks/useCanvasTriggers'
import { getAccessToken, getUser } from '../auth'
import type { Post } from '../types'

type ViewMode = 'read' | 'edit'
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

/**
 * Wrapper that wires CanvasView to its data dependencies and also
 * hosts the single permanent CollabEditor popover for in-card editing
 * (Option-4 hybrid pattern, Content IACP c625d7f9). The popover is
 * always mounted while in canvas mode — Yjs sync stays alive across
 * edit open/close cycles, no remount cost.
 */
function CanvasViewWrapper({
  postId,
  morph,
  mentionItems,
}: {
  postId: number
  morph?: MorphState
  mentionItems?: string[]
}) {
  const queryClient = useQueryClient()
  const { blocks, version } = usePostBlocks(postId)
  const [editingTarget, setEditingTarget] = useState<{
    blockId: string
    paragraphIndex: number
  } | null>(null)
  const editingBlockId = editingTarget?.blockId ?? null
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)

  // Invalidate blocks query whenever an edit popover closes — without
  // this, React Query's staleTime keeps the pre-edit text cached and
  // the card shows the old content even though Yjs already synced
  // the new text into the CRDT. The popover wraps CollabEditor which
  // is Yjs-bound, so edits live in the doc instantly; the card render
  // reads from /crdt/structured REST snapshot which needs a refetch.
  const closeEditPopover = useCallback(() => {
    setEditingTarget(null)
    queryClient.invalidateQueries({ queryKey: ['docStructured', postId] })
  }, [queryClient, postId])

  // When editing opens or layout changes, recompute the card's DOM
  // rect so the popover lands on top of it. Re-poll on a short
  // interval while open to track pan/zoom (cheap, ~10 reads/sec).
  useEffect(() => {
    if (!editingBlockId) {
      setAnchorRect(null)
      return
    }
    const measure = () => {
      const el = document.querySelector(
        `.canvas-card[data-block-id="${cssEsc(editingBlockId)}"]`,
      )
      if (el) setAnchorRect(el.getBoundingClientRect())
    }
    measure()
    const intv = window.setInterval(measure, 120)
    window.addEventListener('resize', measure)
    return () => {
      window.clearInterval(intv)
      window.removeEventListener('resize', measure)
    }
  }, [editingBlockId])

  return (
    <>
      <CanvasView
        postId={postId}
        blocks={blocks}
        docVersion={version}
        morph={morph}
        onCardEdit={(target) => {
          if (target == null) closeEditPopover()
          else setEditingTarget(target)
        }}
        editingBlockId={editingBlockId}
      />
      <CanvasCardEditor
        postId={postId}
        editingBlockId={editingBlockId}
        editingParagraphIndex={editingTarget?.paragraphIndex ?? null}
        anchorRect={anchorRect}
        mentionItems={mentionItems}
        onRequestClose={closeEditPopover}
      />
      <EdgesAutoCreatedToast count={null} />
    </>
  )
}

function cssEsc(value: string): string {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value)
  return value.replace(/["\\]/g, '\\$&')
}

const AUTHOR_TOGGLE_KEY = 'collab_show_authors'

export function PostEditor() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showExport, setShowExport] = useState(false)
  const [showContext, setShowContext] = useState(false)
  // Author-highlight toggle, hosted at the PostEditor level so both
  // the meta-row chip (PostMeta) and the read-mode renderer
  // (CollabReadView) can read/write it. Persisted in localStorage.
  const [showAuthors, setShowAuthors] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AUTHOR_TOGGLE_KEY) === '1'
    } catch {
      return false
    }
  })
  const toggleShowAuthors = useCallback(() => {
    setShowAuthors((prev) => {
      const next = !prev
      try {
        localStorage.setItem(AUTHOR_TOGGLE_KEY, next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])
  const [viewMode, setViewMode] = useState<ViewMode>('read')
  // Phase 5a runtime: 'doc' (the default collab editor) vs 'canvas'
  // (the 2D whiteboard view). Only relevant for collab_enabled posts.
  // Wrapped in useDocCanvasMorph — the morph hook owns the animated
  // transition and exposes openCanvas/closeCanvas/toggle. editorMode
  // is the user-facing target; the hook's state.targetMode plus
  // direction drive the actual mount decision.
  const morph = useDocCanvasMorph('doc')
  const editorMode: EditorMode = morph.state.targetMode
  const setEditorMode = useCallback(
    (mode: EditorMode) => {
      if (mode === 'canvas') morph.openCanvas()
      else morph.closeCanvas()
    },
    [morph],
  )
  // Wheel/keyboard triggers for the morph (pinch-trackpad, Cmd+Scroll).
  // Discovery button is the ModeToggle in the TopBar.
  useCanvasTriggers({
    onOpen: morph.openCanvas,
    onClose: morph.closeCanvas,
    enabled: true,
    ignoreSelector: '.ProseMirror',
  })
  // Show the canvas while the user is in canvas mode OR while a
  // closing animation is still playing back to doc.
  const showCanvas =
    morph.state.targetMode === 'canvas' ||
    morph.state.direction === 'closing'
  const [content, setContent] = useState('')
  const [contentLoaded, setContentLoaded] = useState(false)
  const [lastSelection, setLastSelection] = useState('')
  const [selectionLine, setSelectionLine] = useState<number | null>(null)
  // Issue #15 follow-up: ref-as-state so AnnotationTooltip can attach
  // its delegated mouseover/mouseout listeners on the read-mode markdown
  // container once it mounts. Also drives `useMermaidAsync` which
  // post-processes erDiagram/sequenceDiagram/classDiagram placeholders
  // emitted by markdownConfig for diagram types that beautiful-mermaid
  // can't handle.
  const [readModeRoot, setReadModeRoot] = useState<HTMLElement | null>(null)
  useMermaidAsync(readModeRoot)
  useD2Async(readModeRoot)

  // Phase 2: post-level share modal — opened from the "Teilen"-button
  // in the TopBar. Only owner / tenant-admin / super_admin can grant;
  // backend enforces, modal surfaces 403 cleanly.
  const [showShare, setShowShare] = useState(false)
  // Phase C2: Section-Create-Modal — Button im TopBar öffnet es.
  const [showSectionCreate, setShowSectionCreate] = useState(false)
  // Phase D: Section-Reply-Modal — opens when user clicks "Antworten"
  // on a Question-Section-Card. Holds the target section_id.
  // Reply target state is set on click but read by the Modal component
  // that isn't currently mounted here (the modal moved into the section
  // card). Keep the setter for the click handler, discard the value.
  const [, setReplyTargetSectionId] = useState<string | null>(null)

  // textarea ref + drag state used by handleTextareaDrop (declared
  // further down after post + handleContentChange are in scope).
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [conflict, setConflict] = useState<{ serverContent: string } | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedRef = useRef('')
  const serverVersionRef = useRef('')

  const { data: post, isLoading } = useQuery({
    queryKey: ['post', id],
    queryFn: () => apiClient.getPost(Number(id)),
    enabled: !!id,
  })

  // Sync content from post on first load
  if (post && !contentLoaded) {
    setContent(post.content || '')
    lastSavedRef.current = post.content || ''
    serverVersionRef.current = post.content || ''
    setContentLoaded(true)
  }

  // Auto-save: debounce 2s after last keystroke
  const saveContent = useCallback(async (text: string) => {
    if (!post || text === lastSavedRef.current) return
    setSaveStatus('saving')
    try {
      // Check for conflicts: fetch current server version
      const serverPost = await apiClient.getPost(post.id)
      const serverContent = serverPost.content || ''

      if (serverContent !== serverVersionRef.current && serverContent !== lastSavedRef.current) {
        // Server content changed (by AI or another user) since we last loaded/saved
        setSaveStatus('idle')
        setConflict({ serverContent })
        return
      }

      await apiClient.updatePost(post.id, { content: text })
      lastSavedRef.current = text
      serverVersionRef.current = text
      setSaveStatus('saved')
      queryClient.invalidateQueries({ queryKey: ['post', id] })
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch {
      setSaveStatus('error')
    }
  }, [post, id, queryClient])

  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent)
    setSaveStatus('idle')
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => saveContent(newContent), 2000)
  }, [saveContent])

  // Plain-textarea image drag-and-drop (non-collab posts). Drops any
  // image/file onto the textarea → uploadFile to storage-api → splices
  // markdown link at the cursor. Declared here (after post + handle-
  // ContentChange) so the dep-array lookup doesn't hit the TDZ on the
  // post variable. CollabEditor handles its own equivalent in TipTap.
  const handleTextareaDrop = useCallback(
    async (e: React.DragEvent<HTMLTextAreaElement>) => {
      e.preventDefault()
      setDragOver(false)
      if (!post) return
      const files = Array.from(e.dataTransfer?.files || [])
      if (files.length === 0) return
      setUploading(true)
      try {
        const ta = e.currentTarget
        const cursor = ta.selectionStart ?? ta.value.length
        const currentText = ta.value
        const inserts: string[] = []
        for (const file of files) {
          try {
            const { url } = await uploadFile(file, {
              isPublic: true,
              permanent: true,
              context: `post-${post.id}-textarea-drop`,
            })
            const alt = file.name.replace(/\.[^.]+$/, '') || 'asset'
            const isImage = file.type.startsWith('image/')
            const isVideo = file.type.startsWith('video/')
            if (isImage) {
              inserts.push(`![${alt}](${url})`)
            } else if (isVideo) {
              inserts.push(`<video controls src="${url}"></video>`)
            } else {
              inserts.push(`[${alt}](${url})`)
            }
          } catch (err) {
            console.error('upload failed for', file.name, err)
          }
        }
        if (inserts.length === 0) return
        const insertion =
          (cursor > 0 && currentText[cursor - 1] !== '\n' ? '\n\n' : '') +
          inserts.join('\n\n') +
          '\n'
        const next =
          currentText.slice(0, cursor) + insertion + currentText.slice(cursor)
        handleContentChange(next)
        window.setTimeout(() => {
          if (textareaRef.current) {
            const newPos = cursor + insertion.length
            textareaRef.current.focus()
            textareaRef.current.setSelectionRange(newPos, newPos)
          }
        }, 0)
      } finally {
        setUploading(false)
      }
    },
    [post, handleContentChange],
  )

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
  }, [])

  // Save on view mode switch (edit → read)
  useEffect(() => {
    if (viewMode === 'read' && post && content !== lastSavedRef.current) {
      saveContent(content)
    }
  }, [viewMode])

  const breadcrumb = post
    ? `${post.doc_type || 'post'} / ${post.title}`
    : 'Loading...'

  const wordCount = (content || '').split(/\s+/).filter(Boolean).length

  const handleSelection = useCallback(() => {
    // Delay slightly for mobile (selection finishes after touchend)
    setTimeout(() => {
      const sel = window.getSelection()?.toString()?.trim() || ''
      if (sel && post) {
        setLastSelection(sel)
        const src = (post.content || '').toLowerCase()
        const selLower = sel.toLowerCase()
        const pos = src.indexOf(selLower.slice(0, 20))
        if (pos >= 0) {
          setSelectionLine(src.slice(0, pos).split('\n').length)
        } else {
          const words = selLower.split(/\s+/).slice(0, 3).join(' ')
          const wPos = src.indexOf(words)
          setSelectionLine(wPos >= 0 ? src.slice(0, wPos).split('\n').length : null)
        }
      }
    }, 50)
  }, [post])

  return (
    <>
      <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} breadcrumb={breadcrumb} actions={post && (
        <>
          {(post.collab_enabled || post.doc_type === 'collab_text') && (
            <ModeToggle mode={editorMode} onChange={setEditorMode} />
          )}
          <div className="view-mode-toggle">
            <button className={`view-mode-btn ${viewMode === 'read' ? 'active' : ''}`} onClick={() => setViewMode('read')}>Read</button>
            <button className={`view-mode-btn ${viewMode === 'edit' ? 'active' : ''}`} onClick={() => setViewMode('edit')}>Edit</button>
          </div>
          <button className="topbar-btn" style={{ fontSize: 11, padding: '3px 10px' }}
            onClick={() => {
              if (lastSelection && post && window.CloudChat?.setContext) {
                window.CloudChat.setContext({ post_id: post.id, selection: lastSelection.slice(0, 500), ...(selectionLine ? { line: selectionLine } : {}) })
                setLastSelection(''); setSelectionLine(null)
              }
              window.CloudChat?.open?.()
            }}>
            <i className="fas fa-robot" style={{ marginRight: 4 }} />{lastSelection ? `Ask AI ✦ L${selectionLine || '?'}` : 'Ask AI'}
          </button>
          <button className={`topbar-btn ${showExport ? 'topbar-btn--accent' : ''}`} style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setShowExport(!showExport)}>
            <i className="fas fa-file-pdf" style={{ marginRight: 4 }} /> Export
          </button>
          <button className="topbar-btn" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setShowShare(true)} title="Beitrag teilen">
            <i className="fas fa-share-alt" style={{ marginRight: 4 }} /> Teilen
          </button>
          {post && (post.collab_enabled || post.doc_type === 'collab_text') && (
            <button className="topbar-btn" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setShowSectionCreate(true)} title="Typed Section anlegen (product / question / task)">
              <i className="fas fa-puzzle-piece" style={{ marginRight: 4 }} /> Section
            </button>
          )}
          <button className={`topbar-btn ${showContext ? 'topbar-btn--accent' : ''}`} style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setShowContext(!showContext)} title="Info Panel">
            <i className="fas fa-columns" />
          </button>
        </>
      )} />
      <div className="app">
        <Sidebar open={sidebarOpen} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            <div className="workspace">
              {isLoading && (
                <div className="workspace-inner">
                  <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
                </div>
              )}
              {post && (
                <div className="workspace-inner">
                  <div className="post-id">#{post.id}</div>

                  <h1 className="post-title" contentEditable={viewMode === 'edit'} suppressContentEditableWarning>
                    {post.title}
                  </h1>

                  <PostMeta
                    post={post}
                    showAuthors={showAuthors}
                    onToggleShowAuthors={toggleShowAuthors}
                  />

                  {viewMode === 'edit' && (
                    <div className="editor-toolbar">
                      <button title="Heading 1"><b>H1</b></button>
                      <button title="Heading 2"><b>H2</b></button>
                      <button title="Heading 3"><b>H3</b></button>
                      <span className="sep" />
                      <button title="Bold"><i className="fas fa-bold" /></button>
                      <button title="Italic"><i className="fas fa-italic" /></button>
                      <button title="Link"><i className="fas fa-link" /></button>
                      <button title="Image"><i className="fas fa-image" /></button>
                      <span className="sep" />
                      <button title="Bullet List"><i className="fas fa-list-ul" /></button>
                      <button title="Ordered List"><i className="fas fa-list-ol" /></button>
                      <button title="Code"><i className="fas fa-code" /></button>
                      <button title="Quote"><i className="fas fa-quote-right" /></button>
                      <button title="Table"><i className="fas fa-table" /></button>
                      <span className="sep" />
                      <button title="Fullscreen"><i className="fas fa-expand" /></button>
                    </div>
                  )}

                  {(() => {
                    const docType = post.doc_type
                    // Doc-type-specific viewers (read mode + their own edit mode)
                    if (docType === 'annotation' && viewMode === 'read') {
                      const storageId = (post.metadata_json as Record<string, unknown> | null)?.source_storage_id as number | null
                      return (
                        <div className="editor-content">
                          <AnnotationViewer content={post.content || ''} storageId={storageId ?? null} />
                        </div>
                      )
                    }
                    if (docType === 'review' && viewMode === 'read') {
                      return (
                        <div className="editor-content">
                          <ReviewViewer content={post.content || ''} />
                        </div>
                      )
                    }
                    if (docType === 'story') {
                      return (
                        <div className="editor-content">
                          <StoryViewer
                            post={post}
                            viewMode={viewMode}
                            onSave={(newContent: string) => saveContent(newContent)}
                            isSaving={saveStatus === 'saving'}
                          />
                        </div>
                      )
                    }
                    if (docType === 'audio_guide') {
                      return (
                        <div className="editor-content">
                          <NarrationViewer
                            post={post}
                            viewMode={viewMode}
                            onSave={(newContent: string) => saveContent(newContent)}
                            isSaving={saveStatus === 'saving'}
                          />
                        </div>
                      )
                    }
                    if ((docType === '3dpresentation' || docType === '3d_presentation') && viewMode === 'read') {
                      return (
                        <div className="editor-content">
                          {PRESENTER_3D_ENABLED && <Presentation3DViewer post={post} />}
                        </div>
                      )
                    }
                    // Phase-1 realtime collaboration — orthogonal to doc_type,
                    // opt-in per post via the `collab_enabled` flag. The legacy
                    // `doc_type === 'collab_text'` keeps working as an implicit
                    // opt-in for posts created before the flag existed.
                    //   Edit mode → CollabEditor live (TipTap ProseMirror over WS).
                    //   Read mode → markdown-rendered HTML from posts.content
                    //               (the snapshot is updated every 50 ops or 60s
                    //                from the CRDT; close enough for read-only).
                    if (post.collab_enabled || docType === 'collab_text') {
                      // Canvas mode is orthogonal to read/edit — works in both
                      // because the canvas is read-mostly anyway (drag is local,
                      // CRDT-write-back comes in Phase 5b).
                      if (showCanvas) {
                        // Full-bleed under the TopBar. The morph state
                        // is passed through so card positions/edges
                        // interpolate over the 800ms / 500ms animation.
                        const mentions = Array.isArray(
                          (post.metadata_json as Record<string, unknown> | undefined)
                            ?.notify_authors,
                        )
                          ? ((post.metadata_json as Record<string, unknown>)
                              .notify_authors as string[])
                          : []
                        return (
                          <div className="canvas-fullscreen">
                            <CanvasViewWrapper
                              postId={post.id}
                              morph={morph.state}
                              mentionItems={mentions}
                            />
                            <SalienceOverlay postId={post.id} />
                          </div>
                        )
                      }
                      if (viewMode === 'read') {
                        // Read-mode for collab posts must show the LIVE
                        // CRDT state, not the post.content snapshot which
                        // is only refreshed every 50 ops or 60s by the
                        // server snapshot loop. CollabReadView polls
                        // /crdt/text directly so an agent write becomes
                        // visible in Read mode within ~8s.
                        return (
                          <CollabReadView
                            post={post}
                            showAuthors={showAuthors}
                            renderHtml={(md) =>
                              post.content_type === 'md'
                                ? renderMarkdown(md, post.title, post.annotations)
                                : `<pre>${escapeHtml(md)}</pre>`
                            }
                            onReplyToSection={(sid) => setReplyTargetSectionId(sid)}
                            currentUserName={(() => {
                              const u = getUser()
                              if (!u) return undefined
                              return (
                                `${u.first_name} ${u.last_name}`.trim() ||
                                u.email
                              )
                            })()}
                          />
                        )
                      }
                      const authUser = getUser()
                      const token = getAccessToken()
                      const user = {
                        id: authUser ? `user:${authUser.id}` : 'anon',
                        name: authUser
                          ? `${authUser.first_name} ${authUser.last_name}`.trim() || authUser.email
                          : 'Anon',
                        color: colorFromId(authUser?.id || 'anon'),
                      }
                      return (
                        <div className="editor-content">
                          <CollabEditor
                            postId={post.id}
                            token={token}
                            user={user}
                            readOnly={false}
                            placeholder="Schreibe los — Aenderungen werden live geteilt."
                            mentionItems={
                              Array.isArray(
                                (post.metadata_json as Record<string, unknown> | undefined)
                                  ?.notify_authors
                              )
                                ? ((post.metadata_json as Record<string, unknown>)
                                    .notify_authors as string[])
                                : []
                            }
                          />
                          <SalienceOverlay postId={post.id} />
                        </div>
                      )
                    }
                    // Default: markdown render in read, textarea in edit
                    if (viewMode === 'read') {
                      return (
                        <>
                          <div
                            ref={(el) => setReadModeRoot(el)}
                            className="editor-content"
                            onMouseUp={handleSelection}
                            onTouchEnd={handleSelection}
                            onContextMenu={(e) => {
                              const sel = window.getSelection()?.toString()?.trim() || ''
                              if (sel) {
                                e.preventDefault()
                                setLastSelection(sel)
                                const src = (post.content || '').toLowerCase()
                                const selLower = sel.toLowerCase()
                                const pos = src.indexOf(selLower.slice(0, 20))
                                if (pos >= 0) {
                                  setSelectionLine(src.slice(0, pos).split('\n').length)
                                } else {
                                  const words = selLower.split(/\s+/).slice(0, 3).join(' ')
                                  const wPos = src.indexOf(words)
                                  setSelectionLine(wPos >= 0 ? src.slice(0, wPos).split('\n').length : null)
                                }
                                setContextMenu({ x: e.clientX, y: e.clientY })
                              }
                            }}
                            onClick={() => setContextMenu(null)}
                            dangerouslySetInnerHTML={{
                              __html: renderByContentType(post)
                            }}
                          />
                          <AnnotationTooltip
                            editorRoot={readModeRoot}
                            annotations={(post.annotations || []).map((a) => ({
                              id: a.id,
                              start_pos: a.start_pos,
                              end_pos: a.end_pos,
                              note_text: a.note_text,
                              author_id: a.author_id,
                              author_name: a.author_name,
                              created_at: a.created_at,
                              source: 'db' as const,
                              replies: (a.replies || []).map((r) => ({
                                text: r.note_text,
                                author_id: r.author_id,
                                author_name: r.author_name,
                                created_at: r.created_at,
                              })),
                            }))}
                          />
                        </>
                      )
                    }
                    return (
                      <div
                        style={{
                          position: 'relative',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <textarea
                          ref={textareaRef}
                          className="editor-textarea"
                          value={content}
                          onChange={(e) => handleContentChange(e.target.value)}
                          onSelect={(e) => {
                            const ta = e.target as HTMLTextAreaElement
                            const sel = ta.value.substring(ta.selectionStart, ta.selectionEnd).trim()
                            if (sel) setLastSelection(sel)
                          }}
                          onDragEnter={(e) => {
                            if (e.dataTransfer?.types?.includes('Files')) {
                              e.preventDefault()
                              setDragOver(true)
                            }
                          }}
                          onDragOver={(e) => {
                            if (e.dataTransfer?.types?.includes('Files')) {
                              e.preventDefault()
                              e.dataTransfer.dropEffect = 'copy'
                            }
                          }}
                          onDragLeave={(e) => {
                            // Only clear when leaving the textarea bounds, not
                            // when crossing child elements (relatedTarget null
                            // means we left the window).
                            const rel = e.relatedTarget as Node | null
                            if (!rel || !e.currentTarget.contains(rel)) {
                              setDragOver(false)
                            }
                          }}
                          onDrop={(e) => void handleTextareaDrop(e)}
                          placeholder="Schreibe hier... (Bilder hier reinziehen)"
                          style={
                            dragOver
                              ? {
                                  outline: '3px dashed var(--accent, #4a6cf7)',
                                  outlineOffset: -3,
                                  background: 'rgba(74, 108, 247, 0.04)',
                                }
                              : undefined
                          }
                        />
                        {(dragOver || uploading) && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              padding: '6px 12px',
                              background: 'var(--accent, #4a6cf7)',
                              color: 'white',
                              borderRadius: 6,
                              fontSize: 13,
                              pointerEvents: 'none',
                            }}
                          >
                            {uploading ? 'Lade hoch …' : 'Loslassen zum Hochladen'}
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* Conflict resolution banner */}
                  {conflict && (
                    <div className="conflict-banner">
                      <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }} />
                      <span>This document was modified externally (e.g. by AI). Choose:</span>
                      <button onClick={() => {
                        // Keep my version — force save
                        setSaveStatus('saving')
                        apiClient.updatePost(post.id, { content }).then(() => {
                          lastSavedRef.current = content
                          serverVersionRef.current = content
                          setSaveStatus('saved')
                          setConflict(null)
                          queryClient.invalidateQueries({ queryKey: ['post', id] })
                        })
                      }}>
                        Keep mine
                      </button>
                      <button onClick={() => {
                        // Accept server version
                        setContent(conflict.serverContent)
                        lastSavedRef.current = conflict.serverContent
                        serverVersionRef.current = conflict.serverContent
                        setConflict(null)
                        setSaveStatus('saved')
                      }}>
                        Accept theirs
                      </button>
                      <button onClick={() => setConflict(null)} style={{ color: 'var(--text-muted)' }}>
                        Dismiss
                      </button>
                    </div>
                  )}

                  <div className="editor-footer">
                    <span>{viewMode === 'edit' ? 'Editing' : 'Markdown'}</span>
                    <span>{wordCount} words</span>
                    <span>{Math.ceil(wordCount / 200)} min read</span>
                    {saveStatus === 'saving' && <span style={{ color: 'var(--color-warning)' }}><i className="fas fa-circle-notch fa-spin" style={{ marginRight: 4 }} />Saving...</span>}
                    {saveStatus === 'saved' && <span className="saved"><i className="fas fa-check" style={{ marginRight: 4 }} />Saved</span>}
                    {saveStatus === 'error' && <span style={{ color: 'var(--color-error)' }}><i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }} />Error</span>}
                    {saveStatus === 'idle' && content !== lastSavedRef.current && <span style={{ color: 'var(--text-muted)' }}>Unsaved changes</span>}
                    {saveStatus === 'idle' && content === lastSavedRef.current && <span className="saved"><i className="fas fa-check" style={{ marginRight: 4 }} />Saved</span>}
                  </div>
                  {/* Selection info bar */}
                  {lastSelection && (
                    <div className="selection-info">
                      <i className="fas fa-i-cursor" style={{ marginRight: 6 }} />
                      <span className="selection-text">"{lastSelection.slice(0, 60)}{lastSelection.length > 60 ? '...' : ''}"</span>
                      {selectionLine && <span className="selection-line">Line {selectionLine}</span>}
                      <button className="selection-action" onClick={() => { setLastSelection(''); setSelectionLine(null) }}>
                        <i className="fas fa-times" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Context menu on right-click */}
              {contextMenu && (
                <div
                  className="context-menu-overlay"
                  onClick={() => setContextMenu(null)}
                >
                  <div
                    className="context-menu"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button onClick={() => {
                      // TODO: open annotation form
                      setContextMenu(null)
                    }}>
                      <i className="fas fa-comment-dots" /> Add Annotation
                    </button>
                    <button onClick={() => {
                      if (lastSelection && post && window.CloudChat?.setContext) {
                        window.CloudChat.setContext({
                          post_id: post.id,
                          selection: lastSelection.slice(0, 500),
                          ...(selectionLine ? { line: selectionLine } : {}),
                        })
                        setLastSelection('')
                        setSelectionLine(null)
                      }
                      window.CloudChat?.open?.()
                      setContextMenu(null)
                    }}>
                      <i className="fas fa-robot" /> Ask AI
                    </button>
                    <button onClick={() => {
                      navigator.clipboard.writeText(lastSelection)
                      setContextMenu(null)
                    }}>
                      <i className="fas fa-copy" /> Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
            {showContext && <ContextPanel post={post} />}
          </div>
          {showExport && post && (
            <>
              <div className="export-overlay-backdrop" onClick={() => setShowExport(false)} />
              <ExportBar post={post} />
            </>
          )}
        </div>
      </div>
      <AgentPanel post={post} />
      {post && (
        <PostShareModal
          postId={post.id}
          open={showShare}
          onClose={() => setShowShare(false)}
        />
      )}
      {post && (
        <SectionCreateModal
          postId={post.id}
          open={showSectionCreate}
          onClose={() => setShowSectionCreate(false)}
          onCreated={() => {
            // Trigger a structured refetch in CollabReadView (8s poll
            // picks it up naturally; we could bump via a query-key bump
            // later for instant feedback).
          }}
        />
      )}
      {/* SectionReplyModal deprecated 2026-06-01 — inline form lives
          in the section card footer now (InlineSection InlineReplyForm).
          The mount is kept commented for trace-of-removal. */}
    </>
  )
}

// Doc-types whose canonical body is plain markdown (or "text-shaped") and
// can therefore be hosted by the Yjs Text CRDT we ship today. Structured
// doctypes (story / audio_guide / annotation / 3dpres / review) keep their
// own editors and would need a per-doctype CRDT schema (Y.Map / Y.Array
// + dedicated frontend binding) to be made realtime — that's a future
// build, not a switch.
const COLLAB_CAPABLE_DOCTYPES = new Set<string>([
  '', 'docu', 'code', 'beitrag', 'text', 'note', 'collab_text',
])
function isCollabCapable(doc_type: string | null | undefined): boolean {
  return COLLAB_CAPABLE_DOCTYPES.has((doc_type || '').toString())
}

function PostMeta({
  post,
  showAuthors,
  onToggleShowAuthors,
}: {
  post: Post
  showAuthors: boolean
  onToggleShowAuthors: () => void
}) {
  const queryClient = useQueryClient()
  const [changingLang, setChangingLang] = useState(false)
  const [togglingCollab, setTogglingCollab] = useState(false)
  // Alex 2026-06-03: Tags inline-edit. Klick auf chip → input,
  // Enter/blur saved. Backend speichert tags als plain lowercase
  // string array — kein #-Präfix.
  const [tagsEditing, setTagsEditing] = useState(false)
  const [tagsDraft, setTagsDraft] = useState('')

  const handleLanguageChange = async (newLang: string) => {
    if (newLang === post.language) return
    setChangingLang(true)
    await apiClient.updatePost(post.id, { language: newLang })
    queryClient.invalidateQueries({ queryKey: ['post', String(post.id)] })
    setChangingLang(false)
  }

  const collabActive = Boolean(post.collab_enabled) || post.doc_type === 'collab_text'
  const collabCapable = isCollabCapable(post.doc_type)
  const handleCollabToggle = async () => {
    if (togglingCollab) return
    if (!collabActive && !collabCapable) {
      alert(
        `doc_type='${post.doc_type}' has its own editor; orthogonal collab is not yet wired up ` +
        `for this doctype. Currently text-shaped doctypes only ` +
        `(docu / code / beitrag / text / collab_text).`
      )
      return
    }
    setTogglingCollab(true)
    await apiClient.updatePost(post.id, { collab_enabled: !collabActive })
    queryClient.invalidateQueries({ queryKey: ['post', String(post.id)] })
    setTogglingCollab(false)
  }

  return (
    <div className="meta-row">
      {/* P2.9: Static vs CRDT-Live indicator. Sits as the first chip so
          it's noticed before users/bots interact. live = realtime CRDT
          + WS push; static = versioned snapshot only. Helps avoid
          tool-confusion (e.g. doc_apply_text on a static post). */}
      {post.collab_enabled ? (
        <span
          className="meta-chip"
          title="Live-Collab — CRDT-Doc, Edits sind realtime"
          style={{
            color: '#1e3a8a',
            background: '#dbeafe',
            border: '1px solid #93c5fd',
          }}
        >
          ● live
        </span>
      ) : (
        <span
          className="meta-chip"
          title="Statisches Dokument — versioned snapshot"
          style={{
            color: '#6b7280',
            background: '#f3f4f6',
          }}
        >
          static
        </span>
      )}
      <span className="meta-chip">
        <span className="label">Author</span> {post.author_name}
      </span>
      <span className="meta-chip" style={{ padding: 0 }}>
        <select
          value={post.language || 'de'}
          onChange={(e) => handleLanguageChange(e.target.value)}
          disabled={changingLang}
          style={{
            border: 'none', background: 'transparent', font: 'inherit',
            fontSize: 12, padding: '3px 10px', cursor: 'pointer', color: 'inherit',
          }}
        >
          <option value="de">DE</option>
          <option value="en">EN</option>
          <option value="sl">SL</option>
          <option value="it">IT</option>
          <option value="es">ES</option>
        </select>
      </span>
      {post.doc_type && (
        <span className="meta-chip">
          <span className="label">Type</span> {post.doc_type}
        </span>
      )}
      {tagsEditing ? (
        <span className="meta-chip meta-chip--editing">
          <span className="label">Tags</span>
          <input
            type="text"
            className="meta-chip-input"
            autoFocus
            value={tagsDraft}
            onChange={(e) => setTagsDraft(e.target.value)}
            onBlur={async () => {
              const next = tagsDraft
                .split(',')
                .map((t: string) => t.trim().toLowerCase().replace(/^#+/, ''))
                .filter(Boolean)
              await apiClient.updatePost(post.id, { tags: next })
              setTagsEditing(false)
              queryClient.invalidateQueries({ queryKey: ['post', String(post.id)] })
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                ;(e.target as HTMLInputElement).blur()
              } else if (e.key === 'Escape') {
                setTagsEditing(false)
              }
            }}
            placeholder="tag1, tag2, tag3 (ohne #)"
            style={{ width: 220 }}
          />
        </span>
      ) : (
        <span
          className="meta-chip meta-chip--clickable"
          onClick={() => {
            setTagsDraft((post.tags || []).join(', '))
            setTagsEditing(true)
          }}
          title="Klick zum Bearbeiten — kommagetrennt, ohne # eingeben"
        >
          <span className="label">Tags</span>{' '}
          {post.tags && post.tags.length > 0 ? (
            <>
              {post.tags.slice(0, 3).join(', ')}
              {post.tags.length > 3 && ` +${post.tags.length - 3}`}
            </>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>+ hinzufügen</span>
          )}
        </span>
      )}
      {post.subtitle && (
        <span className="meta-chip">
          <span className="label">Subtitle</span> {post.subtitle}
        </span>
      )}
      <span className="meta-chip meta-chip--status">{post.status}</span>
      <span
        className={`meta-chip ${collabActive ? 'meta-chip--collab' : ''}`}
        onClick={handleCollabToggle}
        title={
          collabActive
            ? 'Live multi-user editing active — click to turn off'
            : collabCapable
              ? 'Click to enable live multi-user editing'
              : `doc_type='${post.doc_type}' not yet collab-enabled — text doctypes only`
        }
        style={{
          cursor: 'pointer',
          opacity: togglingCollab ? 0.5 : (collabActive || collabCapable) ? 1 : 0.5,
        }}
      >
        <span className="label">Collab</span>
        {collabActive ? 'live' : 'off'}
      </span>
      {collabActive && <NotifyAuthorsPicker post={post} />}
      {collabActive && (
        <span
          className={`meta-chip ${showAuthors ? 'meta-chip--authors-on' : ''}`}
          onClick={onToggleShowAuthors}
          title={
            showAuthors
              ? 'Author-Farben werden angezeigt — klick zum Ausblenden'
              : 'Author-Farben einblenden (zeigt pro Absatz wer geschrieben hat)'
          }
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <span className="label">Autoren</span>
          {showAuthors ? 'an' : 'aus'}
        </span>
      )}
      {post.logo_url && (
        <span className="meta-chip meta-chip--logo">
          <span className="label">Logo</span>
          <img src={post.logo_url.split(',')[0]} alt="logo" />
        </span>
      )}
    </div>
  )
}

/**
 * Pick the right HTML render strategy from the post's content_type meta:
 *   - 'md'        → render through marked.js (markdown → HTML)
 *   - 'html'      → trust as-is (already HTML; sanitization is the
 *                   author's responsibility on this tenant)
 *   - default     → escape + wrap in <pre> (plain text / json / unknown)
 */
function renderByContentType(post: Post): string {
  const ct = post.content_type
  const content = post.content || ''
  if (ct === 'md') {
    return renderMarkdown(content, post.title, post.annotations)
  }
  if (ct === 'html') {
    return content
  }
  return `<pre>${escapeHtml(content)}</pre>`
}

function escapeHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function renderMarkdown(md: string, title: string, annotations?: Post['annotations']): string {
  // Strip duplicate H1 if it matches the post title
  let content = md
  const firstLine = content.split('\n')[0]?.trim() || ''
  if (firstLine.startsWith('# ')) {
    const h1 = firstLine.slice(2).trim().toLowerCase()
    const t = title.trim().toLowerCase()
    if (t.startsWith(h1) || h1.startsWith(t)) {
      content = content.split('\n').slice(1).join('\n').replace(/^\n+/, '')
    }
  }

  let html = parseMarkdown(content)

  // Inject annotation highlights into rendered HTML.
  //
  // Approach (Issue #15 follow-up #2): strip markdown formatting from
  // the snippet (** __ * _ ` and heading-prefix) so a snippet like
  // `**Spec Content-Post #810**` searches as `Spec Content-Post #810`
  // and matches the rendered `<strong>Spec Content-Post #810</strong>`.
  // Then split the html at the first case-insensitive match and splice
  // a <mark> around the matched substring. Direct text-search (no
  // regex on the needle) avoids regex-special-char escaping bugs and
  // is robust to MarkDown→HTML structural changes that the previous
  // implementation lost most annotations to.
  //
  // Process in descending start_pos order so earlier marks don't
  // shift positions for later ones.
  if (annotations && annotations.length > 0) {
    const sorted = [...annotations]
      .filter(a => a.start_pos >= 0 && a.end_pos > a.start_pos)
      .sort((a, b) => b.start_pos - a.start_pos)

    for (const ann of sorted) {
      const raw = md.slice(ann.start_pos, Math.min(ann.end_pos, ann.start_pos + 100))
      if (!raw.trim()) continue
      // Strip outer markdown chars + heading prefix + collapse whitespace
      const cleaned = raw
        .replace(/^#{1,6}\s+/, '')
        .replace(/[*_`~]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
      if (cleaned.length < 3) continue
      // Use only the first ~30 chars of the cleaned snippet — short
      // enough to land inside a single text node after MarkDown
      // rendering, long enough to be unique.
      const needle = cleaned.slice(0, 30)
      const haystackLower = html.toLowerCase()
      const idx = haystackLower.indexOf(needle.toLowerCase())
      if (idx < 0) continue
      // Splice <mark> around the original-case match — `data-annotation-id`
      // is the authoritative anchor the AnnotationTooltip walks on hover.
      const before = html.slice(0, idx)
      const matched = html.slice(idx, idx + needle.length)
      const after = html.slice(idx + needle.length)
      html =
        before +
        `<mark class="annotation-mark" data-annotation-id="${ann.id}">` +
        matched +
        `</mark>` +
        after
    }
  }

  return html
}
